import { useEffect, useState } from "react";
import { Form, FormField, FormItem, FormLabel, FormControl } from "../components/ui/form";
import { Input } from "../components/ui/input";
import { Select, SelectItem } from "../components/ui/select";
import axios from "axios";
import { useKeycloak } from "../providers/keycloak-provider";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import DashboardLayout, { LayoutProps } from "../components/DashboardLayout";

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  cpf?: string;
}

const ROLES = ["user", "admin"];
const ROLE_LABELS: Record<string, string> = {
  user:  "Usuário",
  admin: "Admin",
};
const ROLE_COLORS: Record<string, string> = {
  user:  "bg-gray-100 text-gray-700",
  admin: "bg-blue-100 text-blue-700",
};

const EMPTY_FORM = { name: "", email: "", cpf: "", password: "", role: "user" };

interface Props extends LayoutProps {
  listPath: string;
}

export default function UsuariosPage({ listPath, csrfToken, flash }: Props) {
  const { keycloak } = useKeycloak();
  const [users, setUsers]           = useState<User[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState<User | null>(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [formError, setFormError]   = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const availableRoles = ROLES;

  function fetchUsers() {
    setLoading(true);
    axios.get(listPath, { headers: { Authorization: `Bearer ${keycloak.token ?? ""}` } })
      .then(r => { setUsers(r.data); setError(null); })
      .catch(() => setError("Erro ao carregar usuários."))
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchUsers(); }, []);

  function openNew() {
    setForm(EMPTY_FORM);
    setEditing(null);
    setFormError(null);
    setShowForm(true);
  }

  function openEdit(u: User) {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role , cpf: u.cpf ?? ""   });
    setFormError(null);
    setShowForm(true);
  }

  async function handleSave() {
    setSaving(true);
    setFormError(null);
    try {
      const headers = { "X-CSRF-Token": csrfToken, "Content-Type": "application/json", "Authorization": `Bearer ${keycloak.token ?? ""}` };
      const body    = editing
        ? { user: { ...form, ...(form.password ? {} : { password: undefined }) } }
        : { user: form };

      if (editing) {
        await axios.patch(`/api/v1/admin/usuarios/${editing.id}`, body, { headers });
      } else {
        await axios.post("/api/v1/admin/usuarios", body, { headers });
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditing(null);
      fetchUsers();
    } catch (e: any) {
      const msgs = e.response?.data?.errors;
      setFormError(Array.isArray(msgs) ? msgs.join(", ") : "Erro ao salvar usuário.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/api/v1/admin/usuarios/${deleteTarget.id}`, {
        headers: { "X-CSRF-Token": csrfToken, "Authorization": `Bearer ${keycloak.token ?? ""}` },
      });
      setDeleteTarget(null);
      fetchUsers();
    } catch (e: any) {
      alert(e.response?.data?.error || "Erro ao excluir usuário.");
    } finally {
      setDeleteLoading(false);
    }
  }

  // Handler para garantir reset ao fechar o modal
  function handleFormModalChange(open: boolean) {
    setShowForm(open);
    if (!open) {
      setForm(EMPTY_FORM);
      setEditing(null);
    }
  }

  return (
    <DashboardLayout flash={flash}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-800">Gerenciar Usuários</h1>
          <Button onClick={openNew} className="bg-blue-500">+ Novo Usuário</Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        {loading ? (
          <p className="text-gray-500 text-sm">Carregando...</p>
        ) : (
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Nome</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">E-mail</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Perfil</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{u.name}</td>
                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[u.role] ?? "bg-gray-100 text-gray-700"}`}>
                        {ROLE_LABELS[u.role] ?? u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button
                        onClick={() => openEdit(u)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium cursor-pointer"
                      >
                        Editar
                      </button>
                      <button
                          onClick={() => setDeleteTarget(u)}
                          className="text-red-500 hover:text-red-700 text-xs font-medium cursor-pointer"
                        >
                          Excluir
                        </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                      Nenhum usuário cadastrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal criar/editar ── */}
      <Dialog open={showForm} onOpenChange={handleFormModalChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Usuário" : "Novo Usuário"}</DialogTitle>
            <DialogDescription>
              {editing ? "Altere os dados do usuário." : "Preencha os dados para criar um novo usuário."}
            </DialogDescription>
          </DialogHeader>

          {formError && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-3 py-2 rounded text-sm">{formError}</div>
          )}

          <Form>
            <div className="space-y-3">
              <FormField>
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      value={form.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, name: e.target.value }))}
                    />
                  </FormControl>
                </FormItem>
              </FormField>
              <FormField>
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, email: e.target.value }))}
                    />
                  </FormControl>
                </FormItem>
              </FormField>
              {!editing && (
                <FormField>
                  <FormItem>
                    <FormLabel>CPF</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        value={form.cpf}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, cpf: e.target.value }))}
                        placeholder="000.000.000-00"
                      />
                    </FormControl>
                  </FormItem>
                </FormField>
              )}
              <FormField>
                <FormItem>
                  <FormLabel>{editing ? "Nova Senha (deixe em branco para manter)" : "Senha"}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      value={form.password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, password: e.target.value }))}
                      placeholder={editing ? "••••••••" : "Mínimo 8 caracteres"}
                    />
                  </FormControl>
                </FormItem>
              </FormField>
             
            </div>
          </Form>

          <DialogFooter>
            <Button variant="outline" className="cursor-pointer" onClick={() => setShowForm(false)} disabled={saving}>Cancelar</Button>
            <Button variant={"success"} className="cursor-pointer hover:bg-green-600" onClick={handleSave} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Modal confirmar exclusão ── */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir Usuário</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir <strong>{deleteTarget?.name}</strong>? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="cursor-pointer" onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>Cancelar</Button>
            <Button variant="destructive" className="cursor-pointer" onClick={handleDelete} disabled={deleteLoading}>
              {deleteLoading ? "Excluindo..." : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
